// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — hallBuild.jsx   ·   THE HALL OF DEEDS, INSIDE
// ────────────────────────────────────────────────────────────────────
// The architecture, the trophies, the gallery wall and the Deed Book of
// the 3D Hall. Built from the SAME kit as the portico on the boardwalk
// (`HallOfDeeds` in pierPieces.jsx): dressed stone tinted #d8d0e2, gold
// with a warm emissive, violet banners, poolTex light painted on.
//
// ⚑ A KIT, NOT A SCREEN. It imports no screen and no game logic. Every
// placement comes in as data from PierHome's layout (`getHallLayout()`),
// and every tile is turned into world space by ONE number, `S`.
//
// THE LAWS (the pier's, unchanged):
//  · ZERO LIGHTS. Sconces, trophy spots, windows and doors are emissive,
//    basic or additive — or pools painted on the stone.
//  · MERGED BY MATERIAL. A wall with a hundred stones is a few draws.
//  · NO PRNG DURING RENDER. Everything seeded inside useMemo.
//  · EVERY TEXTURE THROUGH `canvasTex`, so `releaseTextures()` frees it.
//  · ONE TICK SET. Animation registers in `BUILD_TICKS`; the host mounts
//    one `BuildDriver`.
// ════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { Merger, boxUV, canvasTex, mulberry32, hashId, FONT_GEN } from "./pierTex";
import { stoneTex, timberTex, poolTex, shadowTex } from "./pierPaint";
import { Merged, BUILD_TICKS } from "./pierBuild";
import { Glows } from "./pierPieces";

// ⭐ THE ONE SCALE. World units per hall tile. The 2D map packs a plinth
// on every tile of a bay with one-tile lanes between rows; at 1.0 the
// lanes are a shoulder wide. 1.5 gives 2.5-unit aisles between plinth rows
// while the player stays 1.5 units tall — the map itself is untouched.
export const S = 1.5;

export const GOLD = "#c9a84c";
export const GOLD_HI = "#f0d060";
export const STONE = "#d8d0e2";        // the portico's own tint
const FONT = "Cinzel, 'Palatino Linotype', serif";
const EMOJI = `"Segoe UI Emoji","Apple Color Emoji","Noto Color Emoji",${FONT}`;

export const WALL_H = 6.4;             // to the top of the cornice
export const CEIL_Y = 6.9;             // the coffer soffit
const DADO_H = 0.95;                   // the marble wainscot; the cut-away stub

// Where the room's faces are, from the layout. Tile centres sit on integer
// tile * S; a wall tile's inner face is half a tile in from its centre.
export function roomOf(L) {
  return {
    zN: (L.hallTop + 0.5) * S,
    zS: (L.hallBot - 0.5) * S,
    xW: 0.5 * S,
    xE: (L.cols - 1.5) * S,
  };
}

// ════════════════════════════════════════════════════════════════════
// THE PAINT — every surface of the Hall, cached through canvasTex
// ════════════════════════════════════════════════════════════════════
const wrap = (t) => { t.wrapS = t.wrapT = THREE.RepeatWrapping; return t; };

// Polished aubergine-black marble in two-slab squares, veined, gold seams.
export const marbleTex = () => wrap(canvasTex("hall-marble", 512, (ctx, W) => {
  const R = mulberry32(0x3A7B1E);
  const half = W / 2;
  for (let i = 0; i < 2; i++) for (let j = 0; j < 2; j++) {
    const dark = (i + j) % 2 === 0;
    const g = ctx.createLinearGradient(i * half, j * half, (i + 1) * half, (j + 1) * half);
    g.addColorStop(0, dark ? "#2c2336" : "#4a3e58");
    g.addColorStop(1, dark ? "#211a2a" : "#3a3048");
    ctx.fillStyle = g; ctx.fillRect(i * half, j * half, half, half);
    for (let v = 0; v < 9; v++) {
      ctx.strokeStyle = dark ? `rgba(200,180,230,${0.05 + R() * 0.08})` : `rgba(230,215,245,${0.06 + R() * 0.1})`;
      ctx.lineWidth = 0.6 + R() * 1.8;
      ctx.beginPath();
      let x = i * half + R() * half, y = j * half;
      ctx.moveTo(x, y);
      for (let s = 0; s < 8; s++) { x += (R() - 0.5) * 50; y += half / 8; ctx.lineTo(x, y); }
      ctx.stroke();
    }
  }
  // a sheen band, so it reads polished under a light that cannot reflect
  const sh = ctx.createLinearGradient(0, 0, W, W);
  sh.addColorStop(0.35, "rgba(255,255,255,0)");
  sh.addColorStop(0.5, "rgba(255,245,255,0.05)");
  sh.addColorStop(0.65, "rgba(255,255,255,0)");
  ctx.fillStyle = sh; ctx.fillRect(0, 0, W, W);
  // gold seams
  ctx.fillStyle = "rgba(201,168,76,0.55)";
  ctx.fillRect(0, 0, W, 3); ctx.fillRect(0, 0, 3, W);
  ctx.fillRect(0, half - 1, W, 2); ctx.fillRect(half - 1, 0, 2, W);
}));

// ════════════════════════════════════════════════════════════════════
// ⭐ THE FINISHES AS SURFACES (caP'n, 2026-09-16: "very unique from each
// other, not just slightly different colors"). Each worn finish names a
// `pattern`; each pattern is its own painted surface, and a few carry an
// emissive map so the surface itself glows (still no light).
// ════════════════════════════════════════════════════════════════════
// Split slate: irregular dark flags, pale grout, a wet sheen on some.
const slateTex = () => wrap(canvasTex("hall-slate-v3", 512, (ctx, W) => {
  const R = mulberry32(0x51A7E);
  ctx.fillStyle = "#8a8498"; ctx.fillRect(0, 0, W, W);          // the grout
  // two courses a texture — big flags, not bathroom tile (visual check)
  const rows = 2, rh = W / rows;
  for (let r = 0; r < rows; r++) {
    let x = r % 2 ? -W / 4 : 0;
    while (x < W) {
      const w = W / 2 * (0.6 + R() * 0.7);
      const v = 38 + R() * 26;
      ctx.fillStyle = `rgb(${v},${v - 2},${v + 12})`;
      const j = [0, 0, 0, 0, 0, 0, 0, 0].map(() => (R() - 0.5) * 6);
      // ⚠ tileable (review): a flag that runs off one edge is drawn again on the other
      for (const ox of [-W, 0, W]) {
        ctx.beginPath();
        ctx.moveTo(ox + x + 4 + j[0], r * rh + 4 + j[1]); ctx.lineTo(ox + x + w - 4 + j[2], r * rh + 4 + j[3]);
        ctx.lineTo(ox + x + w - 4 + j[4], r * rh + rh - 4 + j[5]); ctx.lineTo(ox + x + 4 + j[6], r * rh + rh - 4 + j[7]);
        ctx.closePath(); ctx.fill();
      }
      for (let k = 0; k < 5; k++) {                                // cleavage lines
        ctx.strokeStyle = `rgba(160,150,190,${0.08 + R() * 0.1})`; ctx.lineWidth = 1;
        ctx.beginPath(); const yy = r * rh + 8 + R() * (rh - 16);
        ctx.moveTo(x + 6, yy); ctx.lineTo(x + w - 6, yy + (R() - 0.5) * 10); ctx.stroke();
      }
      if (R() > 0.6) {                                             // a wet sheen
        const g = ctx.createRadialGradient(x + w / 2, r * rh + rh / 2, 2, x + w / 2, r * rh + rh / 2, w / 2);
        g.addColorStop(0, "rgba(200,210,255,0.14)"); g.addColorStop(1, "rgba(200,210,255,0)");
        ctx.fillStyle = g; ctx.fillRect(x, r * rh, w, rh);
      }
      x += w;
    }
  }
}));

// Anima-veined marble: WHITE stone, big soft slabs, pink veins — and the
// veins are also the emissive map, so they glow in the dark.
const animaVeins = (ctx, W, R, glow) => {
  for (let v = 0; v < 14; v++) {
    ctx.strokeStyle = glow ? `rgba(255,${90 + R() * 60},${160 + R() * 40},${0.5 + R() * 0.5})` : `rgba(255,120,176,${0.35 + R() * 0.3})`;
    ctx.lineWidth = glow ? 1.5 + R() * 2.5 : 1 + R() * 2;
    // ⚠ tileable (review): the vein ENDS where it began in x, and is drawn
    // at ±W and ±W vertically so its clipped ends meet their other halves
    const x0 = R() * W, pts = [[x0, 0]];
    let x = x0, y = 0;
    while (y < W - 30) { x += (R() - 0.5) * 60; y += 20 + R() * 30; pts.push([x, y]); }
    pts.push([x0, W]);
    for (const ox of [-W, 0, W]) for (const oy of [-W, 0, W]) {
      ctx.beginPath();
      pts.forEach(([px, py], k) => (k ? ctx.lineTo(px + ox, py + oy) : ctx.moveTo(px + ox, py + oy)));
      ctx.stroke();
    }
  }
};
const animaMarbleTex = () => wrap(canvasTex("hall-anima-marble-v3", 512, (ctx, W) => {
  const R = mulberry32(0xA417A);
  const g = ctx.createLinearGradient(0, 0, W, W);
  g.addColorStop(0, "#d6ccd4"); g.addColorStop(1, "#b8aab6");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, W);
  for (let i = 0; i < 40; i++) {                                   // cloudy mineral
    ctx.fillStyle = `rgba(200,180,200,${R() * 0.12})`;
    ctx.beginPath(); ctx.arc(R() * W, R() * W, 20 + R() * 60, 0, 7); ctx.fill();
  }
  animaVeins(ctx, W, mulberry32(0xA417B), false);
  ctx.fillStyle = "rgba(240,208,96,0.4)";                          // fine gold seams, one slab a tile
  ctx.fillRect(0, 0, W, 2); ctx.fillRect(0, 0, 2, W);
}));
const animaGlowTex = () => wrap(canvasTex("hall-anima-glow-v3", 512, (ctx, W) => {
  ctx.fillStyle = "#000"; ctx.fillRect(0, 0, W, W);
  animaVeins(ctx, W, mulberry32(0xA417B), true);
}));

// Trowelled plaster: no blocks at all — sweeps of the trowel, a painted
// frieze band of lozenges, a warm limewash.
// ⚑ scaled so ONE frieze runs round the room: walls are boxUV'd at 0.27/unit
// and stand 6.4 tall, so 0.58 of a repeat per UV unit (review)
const plasterTex = () => { const t = plasterPaint(); t.repeat.set(0.58, 0.58); return t; };
const plasterPaint = () => wrap(canvasTex("hall-plaster", 256, (ctx, W) => {
  const R = mulberry32(0x9A57E);
  ctx.fillStyle = "#d9b49a"; ctx.fillRect(0, 0, W, W);
  for (let i = 0; i < 160; i++) {
    ctx.strokeStyle = `rgba(${R() > 0.5 ? "255,236,220" : "150,100,80"},${0.05 + R() * 0.08})`;
    ctx.lineWidth = 6 + R() * 14;
    ctx.beginPath(); const x = R() * W, y = R() * W;
    ctx.arc(x, y, 20 + R() * 40, R() * 6, R() * 6 + 0.8); ctx.stroke();
  }
  ctx.fillStyle = "#8a4a3a"; ctx.fillRect(0, W * 0.42, W, 14);    // the frieze
  ctx.fillStyle = "#c9a84c";
  for (let x = 8; x < W; x += 32) {
    ctx.beginPath(); ctx.moveTo(x, W * 0.42 + 7); ctx.lineTo(x + 8, W * 0.42 + 2); ctx.lineTo(x + 16, W * 0.42 + 7); ctx.lineTo(x + 8, W * 0.42 + 12); ctx.fill();
  }
  ctx.fillStyle = "rgba(90,50,40,0.5)"; ctx.fillRect(0, W * 0.42 - 3, W, 2); ctx.fillRect(0, W * 0.42 + 15, W, 2);
}));

// N'ullus stone: near-black violet blocks, cut with cracks that glow.
const nullusCrack = (ctx, W, R, glow) => {
  for (let i = 0; i < 5; i++) {
    ctx.strokeStyle = glow ? `rgba(179,136,255,${0.55 + R() * 0.35})` : "rgba(20,8,40,0.9)";
    ctx.lineWidth = glow ? 1 + R() * 1 : 2.5;
    const pts = [[R() * W, R() * W]];
    for (let k = 0; k < 6; k++) { const [px, py] = pts[pts.length - 1]; pts.push([px + (R() - 0.5) * 70, py + (R() - 0.5) * 70]); }
    for (const ox of [-W, 0, W]) for (const oy of [-W, 0, W]) {      // tileable
      ctx.beginPath();
      pts.forEach(([px, py], k) => (k ? ctx.lineTo(px + ox, py + oy) : ctx.moveTo(px + ox, py + oy)));
      ctx.stroke();
    }
  }
};
const nullusStoneTex = () => wrap(canvasTex("hall-nullus-v3", 256, (ctx, W) => {
  const R = mulberry32(0x4011);
  ctx.fillStyle = "#120a1e"; ctx.fillRect(0, 0, W, W);
  const rows = 5, h = W / rows;
  for (let r = 0; r < rows; r++) {
    // courses of whole blocks that sum to exactly W, so it tiles (review)
    const n = 3, off = r % 2 ? W / 6 : 0;
    for (let b = 0; b < n; b++) {
      const w = W / n, x = b * w + off;
      const v = R();
      for (const ox of [-W, 0]) {
        ctx.fillStyle = `rgb(${46 + v * 20},${34 + v * 14},${78 + v * 30})`;
        ctx.fillRect(x + ox + 2, r * h + 2, w - 4, h - 4);
        ctx.fillStyle = "rgba(200,170,255,0.08)"; ctx.fillRect(x + ox + 2, r * h + 2, w - 4, 3);   // a cold arris
      }
    }
  }
  nullusCrack(ctx, W, mulberry32(0x4012), false);
}));
const nullusGlowTex = () => wrap(canvasTex("hall-nullus-glow-v3", 256, (ctx, W) => {
  ctx.fillStyle = "#000"; ctx.fillRect(0, 0, W, W);
  nullusCrack(ctx, W, mulberry32(0x4012), true);
}));

// pattern → { map, emissiveMap, emissive, roughness, metalness }
export function floorSurface(pattern) {
  switch (pattern) {
    case "slate": return { map: slateTex(), emissiveMap: null, emissive: "#000000", roughness: 0.82, metalness: 0.02 };
    case "anima": return { map: animaMarbleTex(), emissiveMap: animaGlowTex(), emissive: "#ff6fae", roughness: 0.3, metalness: 0.08 };
    default:      return { map: marbleTex(), emissiveMap: null, emissive: "#000000", roughness: 0.38, metalness: 0.1 };
  }
}
export function wallSurface(pattern) {
  switch (pattern) {
    case "plaster": return { map: plasterTex(), emissiveMap: null, emissive: "#000000", roughness: 0.96 };
    case "nullus":  return { map: nullusStoneTex(), emissiveMap: nullusGlowTex(), emissive: "#b388ff", roughness: 0.6 };
    default:        return { map: stoneTex(), emissiveMap: null, emissive: "#000000", roughness: 0.9 };
  }
}
// ⚑ applied IN PLACE: swapping a map is a material update, not a rebuild
export function applySurface(mat, surf, color = "#ffffff", glow = 0.9) {
  if (!mat) return;
  if (mat.map !== surf.map || mat.emissiveMap !== surf.emissiveMap) {
    mat.map = surf.map; mat.emissiveMap = surf.emissiveMap; mat.needsUpdate = true;
  }
  mat.color.set(color);
  mat.emissive.set(surf.emissive);
  mat.emissiveIntensity = surf.emissiveMap ? glow : 0;
  mat.roughness = surf.roughness;
  if (surf.metalness != null) mat.metalness = surf.metalness;
}

// The runner — burgundy field, gold borders, a running lozenge. Repeats on u.
export const carpetTex = () => wrap(canvasTex("hall-carpet", 256, (ctx, W, H) => {
  const R = mulberry32(0xCA4E7);
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "#3a1224"); g.addColorStop(0.5, "#5a1c34"); g.addColorStop(1, "#3a1224");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  for (let i = 0; i < 900; i++) {
    ctx.fillStyle = `rgba(${R() < 0.5 ? "0,0,0" : "255,200,220"},${R() * 0.06})`;
    ctx.fillRect(R() * W, R() * H, 2, 2);
  }
  ctx.fillStyle = GOLD; ctx.fillRect(0, 14, W, 7); ctx.fillRect(0, H - 21, W, 7);
  ctx.fillStyle = "#2a0c18"; ctx.fillRect(0, 0, W, 12); ctx.fillRect(0, H - 12, W, 12);
  ctx.fillStyle = "rgba(201,168,76,0.5)"; ctx.fillRect(0, 30, W, 2); ctx.fillRect(0, H - 32, W, 2);
  ctx.fillStyle = "rgba(240,208,96,0.55)";
  const cy = H / 2;
  ctx.beginPath(); ctx.moveTo(W / 2, cy - 44); ctx.lineTo(W / 2 + 60, cy); ctx.lineTo(W / 2, cy + 44); ctx.lineTo(W / 2 - 60, cy); ctx.closePath(); ctx.fill();
  ctx.fillStyle = "#4a1830";
  ctx.beginPath(); ctx.moveTo(W / 2, cy - 30); ctx.lineTo(W / 2 + 40, cy); ctx.lineTo(W / 2, cy + 30); ctx.lineTo(W / 2 - 40, cy); ctx.closePath(); ctx.fill();
  ctx.fillStyle = "rgba(167,139,250,0.6)";
  ctx.beginPath(); ctx.arc(W / 2, cy, 9, 0, 7); ctx.fill();
}));

// A name cut into a dark stone panel, gilt border, the letters in the wing's colour.
export function plaqueTex(label, tint) {
  return canvasTex(`hall-plaque|${FONT_GEN}|${label}|${tint}`, 1024, (ctx, W, H) => {
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, "#231a2e"); g.addColorStop(1, "#140e1c");
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = GOLD; ctx.lineWidth = 7; ctx.strokeRect(8, 8, W - 16, H - 16);
    ctx.strokeStyle = tint + "88"; ctx.lineWidth = 2; ctx.strokeRect(22, 22, W - 44, H - 44);
    let size = 84;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    do { ctx.font = `900 ${size}px ${FONT}`; size -= 4; } while (ctx.measureText(label).width > W - 140 && size > 28);
    ctx.shadowColor = tint; ctx.shadowBlur = 26; ctx.fillStyle = tint;
    ctx.fillText(label, W / 2, H / 2 + 4);
    ctx.shadowBlur = 0; ctx.globalAlpha = 0.55; ctx.fillStyle = "#fff6dc";
    ctx.fillText(label, W / 2, H / 2 + 4); ctx.globalAlpha = 1;
    // the two rosettes
    for (const x of [58, W - 58]) { ctx.beginPath(); ctx.arc(x, H / 2, 12, 0, 7); ctx.fillStyle = GOLD_HI; ctx.fill(); }
  }, 160);
}

// A hanging banner in the wing's colour, its glyph on it, swallowtail hem.
export function wingBannerTex(tint, icon) {
  return canvasTex(`hall-banner|${tint}|${icon}`, 256, (ctx, W, H) => {
    const c = new THREE.Color(tint);
    const dark = c.clone().multiplyScalar(0.42).getStyle();
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, c.clone().multiplyScalar(0.8).getStyle()); g.addColorStop(1, dark);
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    for (let x = 0; x < W; x += 18) { ctx.fillStyle = "rgba(0,0,0,0.08)"; ctx.fillRect(x, 0, 6, H); }
    ctx.strokeStyle = GOLD; ctx.lineWidth = 8; ctx.strokeRect(14, 10, W - 28, H - 70);
    ctx.strokeStyle = GOLD_HI; ctx.lineWidth = 2; ctx.strokeRect(26, 22, W - 52, H - 94);
    ctx.beginPath(); ctx.arc(W / 2, H * 0.38, 70, 0, 7);
    ctx.fillStyle = "rgba(10,6,16,0.55)"; ctx.fill();
    ctx.lineWidth = 5; ctx.strokeStyle = GOLD_HI; ctx.stroke();
    ctx.font = `92px ${EMOJI}`; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillStyle = "#fff"; ctx.fillText(icon || "◈", W / 2, H * 0.38 + 6);
    ctx.fillStyle = GOLD;
    ctx.beginPath(); ctx.moveTo(W / 2, H * 0.62); ctx.lineTo(W / 2 + 30, H * 0.68); ctx.lineTo(W / 2, H * 0.74); ctx.lineTo(W / 2 - 30, H * 0.68); ctx.closePath(); ctx.fill();
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath(); ctx.moveTo(0, H); ctx.lineTo(W / 2, H - 56); ctx.lineTo(W, H); ctx.closePath(); ctx.fill();
    ctx.globalCompositeOperation = "source-over";
  }, 512);
}

// A tall clerestory window: night over the Unstable World, a cracked moon.
export const windowTex = () => canvasTex("hall-window", 128, (ctx, W, H) => {
  const R = mulberry32(0x71D0);
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "#1a0f3a"); g.addColorStop(0.6, "#3a2470"); g.addColorStop(1, "#8a6ac8");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  for (let i = 0; i < 40; i++) { ctx.fillStyle = `rgba(240,235,255,${0.3 + R() * 0.6})`; ctx.fillRect(R() * W, R() * H * 0.6, 1.5, 1.5); }
  ctx.beginPath(); ctx.arc(W * 0.62, H * 0.28, 16, 0, 7); ctx.fillStyle = "#f6e8b8"; ctx.shadowColor = "#f2c14e"; ctx.shadowBlur = 20; ctx.fill(); ctx.shadowBlur = 0;
  ctx.strokeStyle = "#b388ff"; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(W * 0.55, H * 0.22); ctx.lineTo(W * 0.64, H * 0.3); ctx.lineTo(W * 0.6, H * 0.36); ctx.stroke();
  ctx.fillStyle = "#120c1a";
  ctx.fillRect(W / 2 - 3, 0, 6, H); ctx.fillRect(0, H * 0.45, W, 5); ctx.fillRect(0, H * 0.72, W, 5);
  ctx.fillRect(0, 0, 6, H); ctx.fillRect(W - 6, 0, 6, H); ctx.fillRect(0, H - 6, W, 6);
}, 256);

// A shaft of moonlight — additive, fades at both ends.
export const rayTex = () => canvasTex("hall-ray", 64, (ctx, W, H) => {
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "rgba(200,190,255,0)"); g.addColorStop(0.15, "rgba(200,190,255,0.8)");
  g.addColorStop(0.7, "rgba(170,160,240,0.35)"); g.addColorStop(1, "rgba(170,160,240,0)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  const s = ctx.createLinearGradient(0, 0, W, 0);
  s.addColorStop(0, "rgba(0,0,0,1)"); s.addColorStop(0.5, "rgba(0,0,0,0)"); s.addColorStop(1, "rgba(0,0,0,1)");
  ctx.globalCompositeOperation = "destination-out"; ctx.fillStyle = s; ctx.fillRect(0, 0, W, H);
}, 256);

// A leaf of the great door: dark oak, raised panels, gold studs.
export const doorLeafTex = () => canvasTex("hall-doorleaf", 256, (ctx, W, H) => {
  const R = mulberry32(0xD00A);
  ctx.fillStyle = "#3a2618"; ctx.fillRect(0, 0, W, H);
  for (let i = 0; i < 140; i++) {
    ctx.strokeStyle = `rgba(15,8,3,${0.08 + R() * 0.2})`; ctx.lineWidth = 1 + R();
    const x = R() * W; ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x + (R() - 0.5) * 6, H); ctx.stroke();
  }
  const panel = (x, y, w, h) => {
    ctx.fillStyle = "rgba(0,0,0,0.3)"; ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = "rgba(255,220,170,0.2)"; ctx.lineWidth = 3; ctx.strokeRect(x + 6, y + 6, w - 12, h - 12);
    ctx.strokeStyle = "rgba(201,168,76,0.55)"; ctx.lineWidth = 2; ctx.strokeRect(x + 2, y + 2, w - 4, h - 4);
  };
  panel(28, 30, W - 56, 180); panel(28, 240, W - 56, 240);
  ctx.fillStyle = GOLD_HI;
  for (const y of [18, 226, 494]) for (let x = 20; x < W; x += 36) { ctx.beginPath(); ctx.arc(x, y, 4, 0, 7); ctx.fill(); }
  ctx.fillStyle = "#1a1008"; ctx.fillRect(0, 0, 6, H); ctx.fillRect(W - 6, 0, 6, H);
}, 512);

// What you see through the yard door: violet dusk, the holding's lawn, a lantern.
export const outsideTex = () => canvasTex("hall-outside-night", 128, (ctx, W, H) => {
  const R = mulberry32(0x0D75);
  // ⚠ NIGHT, like every clerestory in the Hall. A painted sunset through the
  // yard door while the windows showed the moon read as a sticker (review).
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "#0a0818"); g.addColorStop(0.5, "#241a44"); g.addColorStop(0.62, "#3a2a5a");
  g.addColorStop(0.64, "#142014"); g.addColorStop(1, "#0a120a");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  for (let i = 0; i < 26; i++) { ctx.fillStyle = `rgba(255,255,240,${0.3 + R() * 0.6})`; ctx.fillRect(R() * W, R() * H * 0.55, 1.2, 1.2); }
  ctx.beginPath(); ctx.arc(W * 0.72, H * 0.2, 7, 0, 7); ctx.fillStyle = "#fff4d8";
  ctx.shadowColor = "#fff4d8"; ctx.shadowBlur = 14; ctx.fill(); ctx.shadowBlur = 0;
  ctx.fillStyle = "#0c160c";
  for (let x = 0; x < W + 14; x += 14) { ctx.beginPath(); ctx.arc(x + R() * 6, H * 0.64, 10 + R() * 6, Math.PI, 0); ctx.fill(); }
  for (let i = 0; i < 20; i++) { ctx.fillStyle = `rgba(120,190,90,${0.12 + R() * 0.25})`; ctx.fillRect(R() * W, H * 0.68 + R() * H * 0.3, 2, 3); }
  ctx.fillStyle = "#1a1220"; ctx.fillRect(W * 0.2, H * 0.46, 3, H * 0.2);
  ctx.beginPath(); ctx.arc(W * 0.2 + 1.5, H * 0.45, 5, 0, 7); ctx.fillStyle = "#c8f0a0";
  ctx.shadowColor = "#c8f0a0"; ctx.shadowBlur = 12; ctx.fill();
}, 256);

// The open Deed Book: two pages of gold script, a sigil, lit from inside.
export const bookTex = () => canvasTex("hall-book", 512, (ctx, W, H) => {
  const R = mulberry32(0xB00C);
  for (const side of [0, 1]) {
    const x0 = side * W / 2;
    const g = ctx.createRadialGradient(x0 + W / 4, H / 2, 10, x0 + W / 4, H / 2, W / 3);
    g.addColorStop(0, "#fff6d8"); g.addColorStop(1, "#d8b870");
    ctx.fillStyle = g; ctx.fillRect(x0, 0, W / 2, H);
    ctx.fillStyle = "rgba(90,60,20,0.55)";
    for (let y = 30; y < H - 24; y += 14) {
      const len = (W / 2 - 60) * (0.6 + R() * 0.4);
      ctx.fillRect(x0 + 30, y, len, 3);
    }
  }
  const sh = ctx.createLinearGradient(W / 2 - 30, 0, W / 2 + 30, 0);
  sh.addColorStop(0, "rgba(60,40,10,0)"); sh.addColorStop(0.5, "rgba(60,40,10,0.55)"); sh.addColorStop(1, "rgba(60,40,10,0)");
  ctx.fillStyle = sh; ctx.fillRect(W / 2 - 30, 0, 60, H);
  ctx.beginPath(); ctx.arc(W * 0.75, H * 0.5, 38, 0, 7);
  ctx.strokeStyle = "#8a5cd6"; ctx.lineWidth = 5; ctx.stroke();
  ctx.font = `900 44px ${FONT}`; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillStyle = "#6a3cb6"; ctx.fillText("◈", W * 0.75, H * 0.5 + 2);
}, 256);

// A brass plate with a few engraved words.
export function plateTex(text) {
  return canvasTex(`hall-plate|${FONT_GEN}|${text}`, 256, (ctx, W, H) => {
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, "#e8cc7a"); g.addColorStop(0.5, "#b08a3a"); g.addColorStop(1, "#7a5c22");
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "#5a4012"; ctx.lineWidth = 3; ctx.strokeRect(4, 4, W - 8, H - 8);
    let size = 26;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    do { ctx.font = `700 ${size}px ${FONT}`; size -= 2; } while (ctx.measureText(text).width > W - 24 && size > 10);
    ctx.fillStyle = "#2a1c06"; ctx.fillText(text, W / 2, H / 2 + 1);
  }, 64);
}

// ⚠ THE ART CANNOT BE THE ART. Mint images live on terror-assets.pages.dev
// (`APPROVED_IMAGE_HOSTS` in bazaar.js), which sends no CORS headers, so a
// piece's own image can never become a WebGL texture. A same-origin or
// data: image CAN, and is drawn when one is handed in; everything else is
// a painted study in the piece's provenance colour carrying its title and
// its Sigil Code — the two things that make a copy that copy.
export function artTex(piece, img) {
  const key = `hall-art|${FONT_GEN}|${piece.pieceId}|${piece.title}|${piece.code}|${img ? "img" : "paint"}`;
  return canvasTex(key, 256, (ctx, W, H) => {
    const col = piece.prov?.color || GOLD;
    if (img) {
      const a = img.width / img.height, t = W / H;
      let sw = img.width, sh = img.height, sx = 0, sy = 0;
      if (a > t) { sw = img.height * t; sx = (img.width - sw) / 2; } else { sh = img.width / t; sy = (img.height - sh) / 2; }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, W, H);
      return;
    }
    const R = mulberry32(hashId(String(piece.pieceId || piece.title || "art")));
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, "#0c0818"); g.addColorStop(1, "#1e1232");
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    ctx.globalCompositeOperation = "lighter";
    for (let i = 0; i < 7; i++) {
      const x = R() * W, y = R() * H * 0.7, r = 30 + R() * 90;
      const rg = ctx.createRadialGradient(x, y, 0, x, y, r);
      rg.addColorStop(0, col + "66"); rg.addColorStop(1, col + "00");
      ctx.fillStyle = rg; ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }
    ctx.globalCompositeOperation = "source-over";
    ctx.strokeStyle = col + "aa"; ctx.lineWidth = 2;
    for (let i = 0; i < 5; i++) { ctx.beginPath(); ctx.arc(W / 2, H * 0.36, 16 + i * 14 + R() * 6, R() * 6, R() * 6 + 3); ctx.stroke(); }
    ctx.fillStyle = "rgba(4,2,10,0.72)"; ctx.fillRect(0, H * 0.68, W, H * 0.32);
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    let size = 24; const title = String(piece.title || "Untitled");
    do { ctx.font = `900 ${size}px ${FONT}`; size -= 2; } while (ctx.measureText(title).width > W - 20 && size > 11);
    ctx.fillStyle = "#f6eed8"; ctx.fillText(title, W / 2, H * 0.77);
    ctx.font = `700 13px ${FONT}`; ctx.fillStyle = col;
    ctx.fillText(String(piece.code || ""), W / 2, H * 0.89);
  }, 200);
}

// ⭐ THE DEED ATLAS — every trophy's emblem in ONE texture, so every
// painting face, medal face and plinth plaque in the Hall is one draw.
// Keyed by which deeds are earned; it repaints only when that changes.
export function deedAtlas(trophies, earnedKey, isEarned, display) {
  const n = Math.max(1, trophies.length);
  const cols = Math.ceil(Math.sqrt(n));
  const CELL = 160;
  const size = THREE.MathUtils.ceilPowerOfTwo(cols * CELL);
  const index = new Map();
  trophies.forEach((t, i) => index.set(t.id, i));
  const tex = canvasTex(`hall-deeds|${FONT_GEN}|${n}|${hashId(earnedKey)}`, size, (ctx) => {
    trophies.forEach((t, i) => {
      const x = (i % cols) * CELL, y = Math.floor(i / cols) * CELL;
      const earned = isEarned(t.id);
      const d = display(t, earned);
      const cx = x + CELL / 2, cy = y + CELL / 2;
      const g = ctx.createRadialGradient(cx, cy - 16, 6, cx, cy, CELL * 0.7);
      if (earned) { g.addColorStop(0, t.tint + "cc"); g.addColorStop(0.55, "#2a1a3a"); g.addColorStop(1, "#0c0816"); }
      else { g.addColorStop(0, "#2a2630"); g.addColorStop(1, "#0e0c12"); }
      ctx.fillStyle = g; ctx.fillRect(x, y, CELL, CELL);
      ctx.strokeStyle = earned ? d.rarityTint : "#3a3444"; ctx.lineWidth = 6;
      ctx.strokeRect(x + 4, y + 4, CELL - 8, CELL - 8);
      ctx.font = `64px ${EMOJI}`; ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.globalAlpha = earned ? 1 : 0.28;
      if (earned) { ctx.shadowColor = d.rarityTint; ctx.shadowBlur = 16; }
      ctx.fillStyle = earned ? "#fff" : "#8a8494";
      ctx.fillText(d.masked ? "✶" : d.icon, cx, cy - 12);
      ctx.shadowBlur = 0; ctx.globalAlpha = 1;
      let fs = 17; const name = d.masked ? "SEALED" : String(d.name || "").toUpperCase();
      do { ctx.font = `700 ${fs}px ${FONT}`; fs -= 1; } while (ctx.measureText(name).width > CELL - 18 && fs > 8);
      ctx.fillStyle = earned ? "#f6e8c0" : "#6a6474";
      ctx.fillText(name, cx, y + CELL - 26);
    });
  });
  return { tex, cols, size, CELL, index };
}

// Point a plane's UVs at one atlas cell.
export function cellUV(geo, atlas, i) {
  const { cols, size, CELL } = atlas;
  const u0 = ((i % cols) * CELL) / size, u1 = u0 + CELL / size;
  const v1 = 1 - (Math.floor(i / cols) * CELL) / size, v0 = v1 - CELL / size;
  const uv = geo.attributes.uv;
  // PlaneGeometry order: (0,1) (1,1) (0,0) (1,0)
  uv.setXY(0, u0, v1); uv.setXY(1, u1, v1); uv.setXY(2, u0, v0); uv.setXY(3, u1, v0);
  uv.needsUpdate = true;
  return geo;
}

export { stoneTex, timberTex, poolTex, shadowTex, Merged, BUILD_TICKS, Glows };

// ════════════════════════════════════════════════════════════════════
// ⭐ THE CUT-AWAY — the dollhouse, done by which side the lens is on
// ────────────────────────────────────────────────────────────────────
// The default camera stands SOUTH of the player, and the Gallery Wall is
// the south wall — so the one wall that matters most is the one always
// between the lens and you. A wall (or the ceiling) whose OUTSIDE the
// camera is standing on steps aside down to its marble dado; turn the
// camera round and it stands back up with every frame on it. Same fade
// rule as the pier's `BuildDriver`: transparent only while fading, and
// handed back opaque.
// ⚑ Module-level on purpose: React context does not cross the Canvas.
// ════════════════════════════════════════════════════════════════════
export const CUTS = new Set();

function matsOf(o) {
  return Array.isArray(o.material) ? o.material : o.material ? [o.material] : [];
}

function restoreMats(entry) {
  const g = entry.ref.current;
  if (!g) return;
  g.traverse((o) => {
    for (const m of matsOf(o)) {
      const u = m.userData;
      if (u.ho0 === undefined) continue;
      m.opacity = u.ho0; m.depthWrite = u.hd0;
      if (m.transparent !== u.ht0) { m.transparent = u.ht0; m.needsUpdate = true; }
    }
  });
  g.visible = true;
}

export function CutGroup({ side, room, children }) {
  const ref = useRef();
  const { zN, zS, xW, xE } = room;
  const test = useMemo(() => {
    const m = 0.25;
    switch (side) {
      case "N": return (c) => c.z < zN + m;
      case "S": return (c) => c.z > zS - m;
      case "W": return (c) => c.x < xW + m;
      case "E": return (c) => c.x > xE - m;
      default:  return (c) => c.y > CEIL_Y - 0.35;
    }
  }, [side, zN, zS, xW, xE]);
  useEffect(() => {
    const entry = { ref, test, k: 1, fresh: true };
    CUTS.add(entry);
    return () => { CUTS.delete(entry); restoreMats(entry); };
  }, [test]);
  return <group ref={ref}>{children}</group>;
}

// One pass over every cut group, from the host's single frame driver.
export function driveCuts(camera, dt) {
  const rate = 1 - Math.pow(0.0005, Math.min(dt || 0.016, 0.05));
  CUTS.forEach((e) => {
    const g = e.ref.current; if (!g) return;
    const want = e.test(camera.position) ? 0 : 1;
    if (e.k === want) { e.fresh = false; return; }
    // a wall already out of the way when the room opens is simply gone —
    // no fade on arrival
    if (e.fresh) { e.fresh = false; e.k = want; } else e.k += (want - e.k) * rate;
    if (Math.abs(e.k - want) < 0.01) e.k = want;
    g.visible = e.k > 0;
    const fading = e.k < 1;
    g.traverse((o) => {
      for (const m of matsOf(o)) {
        const u = m.userData;
        if (u.ho0 === undefined) { u.ho0 = m.opacity; u.ht0 = m.transparent; u.hd0 = m.depthWrite; }
        const tr = fading || u.ht0;
        if (m.transparent !== tr) { m.transparent = tr; m.needsUpdate = true; }
        m.opacity = u.ho0 * e.k;
        m.depthWrite = fading ? false : u.hd0;
      }
    });
  });
}

// ════════════════════════════════════════════════════════════════════
// MATERIALS — a fresh set per group, so a fading wall fades alone
// ════════════════════════════════════════════════════════════════════
export const additive = (map, color, opacity) => new THREE.MeshBasicMaterial({
  map, color, transparent: true, opacity, depthWrite: false,
  blending: THREE.AdditiveBlending, toneMapped: false, side: THREE.DoubleSide, fog: false,
});

// ⭐ OW-34 · the worn light reaches every flame and warm pool on the stone
export const FLAME = "#ffb84d", POOL = "#ffcf8a";
export function tintFlames(mats, finish) {
  if (mats.flame) mats.flame.color.set(finish?.flameTint || FLAME);
  if (mats.pool) mats.pool.color.set(finish?.poolTint || POOL);
}

// ⭐ the worn wall surface + the worn light's flames, on one wall's mats
export function dressWall(mats, finish) {
  const pat = finish?.wallPattern;
  applySurface(mats.stone, wallSurface(pat), pat ? (pat === "stone" ? STONE : "#ffffff") : (finish?.wallTint || STONE), 0.9);
  tintFlames(mats, finish);
}

export function makeMats() {
  return {
    stone: new THREE.MeshStandardMaterial({ map: stoneTex(), color: STONE, roughness: 0.9 }),
    trim: new THREE.MeshStandardMaterial({ color: "#e6e0ec", roughness: 0.6 }),
    marble: new THREE.MeshStandardMaterial({ map: marbleTex(), color: "#ffffff", roughness: 0.38, metalness: 0.1 }),
    gold: new THREE.MeshStandardMaterial({ color: GOLD, emissive: "#6a4a10", emissiveIntensity: 0.7, metalness: 0.9, roughness: 0.3 }),
    iron: new THREE.MeshStandardMaterial({ color: "#221c2c", roughness: 0.5, metalness: 0.7 }),
    dark: new THREE.MeshBasicMaterial({ color: "#07050c" }),
    flame: additive(poolTex(), FLAME, 1),
    pool: additive(poolTex(), POOL, 0.42),
  };
}

export function disposeGeos(g) { Object.values(g || {}).forEach((x) => x && x.dispose && x.dispose()); }
export function disposeMats(m) { Object.values(m || {}).forEach((x) => x && x.dispose && x.dispose()); }

// The gutters between bays — where pilasters, beams and floor seams go.
export function guttersOf(L) {
  const xs = L.wings.map((w) => (w.x0 - 1) * S);
  if (L.wings.length) xs.push((L.wings[L.wings.length - 1].x1 + 1) * S);
  return xs;
}

// A sconce: iron bracket, gold cup, a painted flame and a warm wash on
// the stone. `at` = [x, z] on the wall face, `n` = [nx, nz] inward normal.
export function addSconce(m, fx, at, n, y) {
  const [nx, nz] = n;
  const p = (d, yy) => [at[0] + nx * d, yy, at[1] + nz * d];
  const rotY = Math.atan2(nx, nz);
  m.box("iron", 0.07, 0.07, 0.42, p(0.21, y), [0, rotY, 0]);
  m.box("iron", 0.16, 0.34, 0.05, p(0.03, y), [0, rotY, 0]);
  m.cyl("gold", 0.13, 0.06, 0.12, 12, p(0.44, y + 0.06));
  fx.flames.push({ pos: p(0.46, y + 0.34), rotY });
  fx.glows.push({ x: at[0] + nx * 0.46, y: y + 0.3, z: at[1] + nz * 0.46, color: "#ffc978", size: 1.1 });
  fx.pools.push({ pos: p(0.015, y + 0.2), rotY, w: 2.0, h: 2.4 });
}

export function Flames({ flames, mat }) {
  const refs = useRef([]);
  useEffect(() => {
    const fn = (t) => refs.current.forEach((f, i) => {
      if (!f) return;
      const k = 1 + Math.sin(t * 7 + i * 2.1) * 0.12 + Math.sin(t * 13 + i) * 0.06;
      f.scale.set(0.34 * k, 0.62 * k, 1);
    });
    BUILD_TICKS.add(fn);
    return () => { BUILD_TICKS.delete(fn); };
  }, [flames]);
  return flames.map((f, i) => (
    <mesh key={i} ref={(el) => { refs.current[i] = el; }} position={f.pos}
          rotation={[0, f.rotY, 0]} scale={[0.34, 0.62, 1]} material={mat}>
      <planeGeometry args={[1, 1]} />
    </mesh>
  ));
}

export function Pools({ pools, mat }) {
  return pools.map((p, i) => (
    <mesh key={i} position={p.pos} rotation={p.rot || [0, p.rotY || 0, 0]} material={p.mat || mat}>
      <planeGeometry args={[p.w, p.h]} />
    </mesh>
  ));
}

// ════════════════════════════════════════════════════════════════════
// THE FLOOR — polished marble in world space, the runner, the seams
// ════════════════════════════════════════════════════════════════════
const aoTex = () => canvasTex("hall-ao", 64, (ctx, W, H) => {
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "rgba(0,0,0,0.7)"); g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
});
const apronTex = () => canvasTex("hall-apron", 64, (ctx, W, H) => {
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "rgba(20,14,28,1)"); g.addColorStop(1, "rgba(7,5,12,0)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
});

// ⭐ THE FINISH (2026-09-15). `finish` = { floorTint, runnerTint, wallTint }
// from the holding's worn Hall finishes (mineriumCatalogue KIND.HALL_*).
// A tint multiplies the painted map — the marble's veins and the stone's
// blocks stay; the colour changes. Applied to the material in place, so a
// new finish is not a rebuild of the room.
export function HallFloor({ L, finish }) {
  const room = useMemo(() => roomOf(L), [L]);
  const entryZ = L.spawn.y * S;
  const yardZ = L.yardDoor.y * S + 0.4;
  const built = useMemo(() => {
    const { zN, zS, xW, xE } = room;
    const m = new Merger();
    m.box("marble", xE - xW + 1.2, 0.3, zS - zN + 1.2, [(xW + xE) / 2, -0.15, (zN + zS) / 2]);
    // past the cut-away the floor carries on, dimmer, before the dark takes it
    m.box("marbleDim", xE - xW + 1.2, 0.3, 3.2, [(xW + xE) / 2, -0.16, zS + 2.2]);
    for (const gx of guttersOf(L)) m.box("gold", 0.07, 0.012, zS - zN, [gx, 0.006, (zN + zS) / 2]);
    m.box("gold", xE - xW, 0.012, 0.07, [(xW + xE) / 2, 0.006, zN + 0.55]);
    m.box("gold", xE - xW, 0.012, 0.07, [(xW + xE) / 2, 0.006, zS - 0.55]);
    const geos = m.build();
    boxUV(geos.marble, 1 / 3);
    boxUV(geos.marbleDim, 1 / 3);
    // the runner, from the great door to the capstone
    const len = xE - xW - 0.6;
    const runner = new THREE.PlaneGeometry(len, 2.2);
    const uv = runner.attributes.uv;
    for (let i = 0; i < uv.count; i++) uv.setX(i, uv.getX(i) * (len / 2.2));
    return { geos, runner };
  }, [L, room]);

  const mats = useMemo(() => ({
    ...makeMats(),
    carpet: new THREE.MeshStandardMaterial({ map: carpetTex(), roughness: 0.95 }),
    marbleDim: new THREE.MeshStandardMaterial({ map: marbleTex(), color: "#8e84a0", roughness: 0.5 }),
    ao: new THREE.MeshBasicMaterial({ map: aoTex(), transparent: true, depthWrite: false, opacity: 0.8 }),
    apron: new THREE.MeshBasicMaterial({ map: apronTex(), transparent: true, depthWrite: false }),
    violet: additive(poolTex(), "#b8a0ff", 0.34),
    spill: additive(poolTex(), GOLD_HI, 0.5),
    yard: additive(poolTex(), "#c8f0a0", 0.45),
  }), []);
  useEffect(() => () => { disposeGeos(built.geos); built.runner.dispose(); }, [built]);
  useEffect(() => () => disposeMats(mats), [mats]);
  useEffect(() => {
    // a pattern carries its own colour; a finish row without one (an old
    // row, or a future tint-only one) still multiplies the marble
    const surf = floorSurface(finish?.floorPattern);
    const tint = finish?.floorPattern ? "#ffffff" : (finish?.floorTint || "#ffffff");
    applySurface(mats.marble, surf, tint, 0.85);
    applySurface(mats.marbleDim, surf, new THREE.Color(tint).multiply(new THREE.Color("#8e84a0")), 0.35);
    mats.carpet.color.set(finish?.runnerTint || "#ffffff");
  }, [mats, finish]);

  const { zN, zS, xW, xE } = room;
  const cx = (xW + xE) / 2;
  const rayPools = useMemo(() => {
    const out = [];
    for (const w of L.wings) {
      const bx = ((w.x0 + w.x1) / 2) * S;
      for (const s of [-1, 1]) out.push({ pos: [bx + s * 3.7, 0.012, zN + 5.4], rot: [-Math.PI / 2, 0, 0], w: 2.6, h: 1.9 });
    }
    return out;
  }, [L, zN]);

  return (
    <group>
      <Merged geos={built.geos} mats={mats} />
      <mesh geometry={built.runner} material={mats.carpet} position={[cx, 0.014, entryZ]} rotation={[-Math.PI / 2, 0, 0]} />
      {/* the wall meets the floor in shadow, all four sides */}
      <mesh material={mats.ao} position={[cx, 0.008, zN + 0.45]} rotation={[-Math.PI / 2, 0, Math.PI]}><planeGeometry args={[xE - xW, 0.9]} /></mesh>
      <mesh material={mats.ao} position={[cx, 0.008, zS - 0.45]} rotation={[-Math.PI / 2, 0, 0]}><planeGeometry args={[xE - xW, 0.9]} /></mesh>
      <mesh material={mats.ao} position={[xW + 0.45, 0.008, (zN + zS) / 2]} rotation={[-Math.PI / 2, 0, -Math.PI / 2]}><planeGeometry args={[zS - zN, 0.9]} /></mesh>
      <mesh material={mats.ao} position={[xE - 0.45, 0.008, (zN + zS) / 2]} rotation={[-Math.PI / 2, 0, Math.PI / 2]}><planeGeometry args={[zS - zN, 0.9]} /></mesh>
      {/* beyond the cut-away the floor runs out into the dark, not off a cliff */}
      <mesh material={mats.apron} position={[cx, -0.012, zS + 9.8]} rotation={[-Math.PI / 2, 0, 0]}><planeGeometry args={[xE - xW + 1.2, 12]} /></mesh>
      <Pools pools={rayPools} mat={mats.violet} />
      <mesh material={mats.spill} position={[xW + 1.5, 0.016, entryZ]} rotation={[-Math.PI / 2, 0, 0]}><planeGeometry args={[3.2, 3.6]} /></mesh>
      <mesh material={mats.yard} position={[xW + 1.2, 0.016, yardZ]} rotation={[-Math.PI / 2, 0, 0]}><planeGeometry args={[2.6, 2.0]} /></mesh>
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE LONG WALLS — pale stone, marble dado, pilasters at every gutter
// ────────────────────────────────────────────────────────────────────
// `part` is "full" (the north Wall of Renown), or "low"/"upper" — the
// south Gallery Wall in two halves, so the cut-away leaves the dado and
// its gold rail standing as the room's edge.
// ════════════════════════════════════════════════════════════════════
function longWall(m, fx, L, room, side, part) {
  const dir = side === "N" ? 1 : -1;
  const face = side === "N" ? room.zN : room.zS;
  const z = (d) => face + dir * d;
  const len = room.xE - room.xW + 1.0;
  const cx = (room.xW + room.xE) / 2;
  const low = part !== "upper", high = part !== "low";
  const y0 = part === "upper" ? DADO_H + 0.08 : 0;
  const y1 = part === "low" ? DADO_H : WALL_H;
  m.box("stone", len, y1 - y0, 0.5, [cx, (y0 + y1) / 2, z(-0.25)]);
  if (low) {
    m.box("marble", len, DADO_H, 0.08, [cx, DADO_H / 2, z(0.04)]);
    if (part === "low") m.box("gold", len, 0.08, 0.66, [cx, DADO_H + 0.04, z(-0.17)]);
    else m.box("gold", len, 0.07, 0.14, [cx, DADO_H + 0.03, z(0.07)]);
  }
  if (high) {
    m.box("trim", len, 0.42, 0.44, [cx, WALL_H - 0.21, z(0.22)]);
    m.box("gold", len, 0.07, 0.46, [cx, WALL_H - 0.46, z(0.23)]);
    m.box("trim", len, 0.14, 0.3, [cx, WALL_H - 0.56, z(0.15)]);
  }
  for (const gx of guttersOf(L)) {
    if (low) {
      const bh = part === "low" ? DADO_H : 0.4;
      m.box("marble", 1.12, bh, 0.46, [gx, bh / 2, z(0.23)]);      // the pilaster stands on marble, like the dado
    }
    if (high) {
      const b = part === "upper" ? DADO_H + 0.08 : 0.4;
      const t = WALL_H - 0.62;
      m.box("stone", 0.86, t - b, 0.32, [gx, (b + t) / 2, z(0.16)]);
      m.box("trim", 1.12, 0.3, 0.46, [gx, WALL_H - 0.77, z(0.23)]);
      m.box("gold", 0.9, 0.06, 0.36, [gx, 2.25, z(0.18)]);
      m.box("gold", 0.9, 0.06, 0.36, [gx, WALL_H - 0.95, z(0.18)]);
      addSconce(m, fx, [gx, z(0.32)], [0, dir], 3.05);
    }
  }
}

function useWall(buildFn, deps, extraMats) {
  const built = useMemo(() => {
    const m = new Merger();
    const fx = { flames: [], glows: [], pools: [] };
    buildFn(m, fx);
    const geos = m.build();
    // ⚠ 0.35 made toy bricks, and 0.2 made each block half a person tall (reviews)
    if (geos.stone) boxUV(geos.stone, 0.27);
    if (geos.marble) boxUV(geos.marble, 1 / 3);
    return { geos, fx };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mats = useMemo(() => ({ ...makeMats(), ...(extraMats ? extraMats() : {}) }), []);
  useEffect(() => () => disposeGeos(built.geos), [built]);
  useEffect(() => () => disposeMats(mats), [mats]);
  return { built, mats };
}

function WallFx({ built, mats, finish = null }) {
  // ⭐ OW-34: the sconce halos take the worn light's warm colour too, or a
  // blue flame burns inside an amber halo
  const glowTint = finish?.poolTint || null;
  const glows = useMemo(() => (glowTint ? built.fx.glows.map((g) => ({ ...g, color: glowTint })) : built.fx.glows),
    [built, glowTint]);
  return (
    <>
      <Merged geos={built.geos} mats={mats} />
      <Flames flames={built.fx.flames} mat={mats.flame} />
      <Pools pools={built.fx.pools} mat={mats.pool} />
      {glows.length > 0 && <Glows points={glows} size={0.9} />}
    </>
  );
}

function LongWall({ L, room, side, part, finish }) {
  const { built, mats } = useWall((m, fx) => longWall(m, fx, L, room, side, part), [L, room, side, part]);
  useEffect(() => { dressWall(mats, finish); }, [mats, finish]);
  return <WallFx built={built} mats={mats} finish={finish} />;
}

// ════════════════════════════════════════════════════════════════════
// ⭐ ONE BAY PER GAME — a plaque on the stone, a banner in the game's
// colour, two clerestory windows with moonlight falling through them,
// and a wash of the wing's colour on the wall behind the banner.
// ════════════════════════════════════════════════════════════════════
function BayDressing({ L, room, finish = null }) {
  const face = room.zN;
  const bays = useMemo(() => L.wings.map((w) => ({ ...w, bx: ((w.x0 + w.x1) / 2) * S })), [L]);
  const DY = 4.45, DZ = 5.4;
  const rayLen = Math.hypot(DY, DZ), rayA = Math.atan2(-DZ, DY);
  const { built, mats } = useWall((m) => {
    for (const b of bays) {
      m.box("trim", 4.75, 0.92, 0.08, [b.bx, 3.17, face + 0.04]);
      m.box("gold", 4.85, 0.05, 0.12, [b.bx, 3.65, face + 0.06]);
      m.box("gold", 4.85, 0.05, 0.12, [b.bx, 2.69, face + 0.06]);
      m.box("gold", 1.4, 0.06, 0.06, [b.bx, 5.66, face + 0.18]);
      for (const s of [-1, 1]) {
        m.box("iron", 0.05, 0.05, 0.18, [b.bx + s * 0.62, 5.66, face + 0.09]);
        m.add("gold", new THREE.SphereGeometry(0.06, 10, 8), [b.bx + s * 0.74, 5.66, face + 0.18]);
        const wx = b.bx + s * 3.7;
        m.box("trim", 0.14, 2.2, 0.2, [wx - 0.57, 4.47, face + 0.1]);
        m.box("trim", 0.14, 2.2, 0.2, [wx + 0.57, 4.47, face + 0.1]);
        m.box("trim", 1.34, 0.12, 0.28, [wx, 3.36, face + 0.14]);
        m.box("trim", 1.34, 0.18, 0.26, [wx, 5.58, face + 0.13]);
        m.box("gold", 0.22, 0.26, 0.3, [wx, 5.62, face + 0.16]);
      }
    }
  }, [bays, face], () => ({
    window: new THREE.MeshBasicMaterial({ map: windowTex(), toneMapped: false }),
    ray: additive(rayTex(), "#c8b8ff", 0.16),
  }));

  // ⭐ the moonlight through the clerestories takes the worn light's colour,
  // and a moonlit Hall lets far more of it in
  useEffect(() => {
    if (!mats.ray) return;
    mats.ray.color.set(finish?.rayTint || "#c8b8ff");
    const op = 0.16 * (finish?.rayBoost ?? 1);
    // ⚠ the cut-away remembers a material's resting opacity (`ho0`) the first
    // time it fades; tell it the new one, or it restores the old (review)
    if (mats.ray.userData.ho0 !== undefined) mats.ray.userData.ho0 = op;
    mats.ray.opacity = op;           // a steady wall is never re-written by the cut-away
  }, [mats, finish]);

  const dressing = useMemo(() => bays.map((b) => ({
    ...b,
    plaque: new THREE.MeshBasicMaterial({ map: plaqueTex(b.label, b.tint), toneMapped: false }),
    banner: new THREE.MeshStandardMaterial({
      map: wingBannerTex(b.tint, b.icon), side: THREE.DoubleSide, transparent: true, alphaTest: 0.4,
      roughness: 0.9, emissive: b.tint, emissiveIntensity: 0.12,
    }),
    wash: additive(poolTex(), b.tint, 0.3),
  })), [bays]);
  useEffect(() => () => dressing.forEach((d) => { d.plaque.dispose(); d.banner.dispose(); d.wash.dispose(); }), [dressing]);

  return (
    <group>
      <Merged geos={built.geos} mats={mats} />
      {dressing.map((b) => (
        <group key={b.game}>
          <mesh material={b.wash} position={[b.bx, 4.6, face + 0.03]}><planeGeometry args={[3.6, 3.6]} /></mesh>
          <mesh material={b.plaque} position={[b.bx, 3.17, face + 0.085]}><planeGeometry args={[4.55, 0.71]} /></mesh>
          <mesh material={b.banner} position={[b.bx, 4.72, face + 0.17]}><planeGeometry args={[0.98, 1.96]} /></mesh>
          {[-1, 1].map((s) => (
            <group key={s}>
              <mesh material={mats.window} position={[b.bx + s * 3.7, 4.47, face + 0.012]}><planeGeometry args={[1.0, 2.0]} /></mesh>
              <mesh material={mats.ray} position={[b.bx + s * 3.7, DY / 2 + 0.05, face + DZ / 2]} rotation={[rayA, 0, 0]}>
                <planeGeometry args={[1.1, rayLen]} />
              </mesh>
            </group>
          ))}
        </group>
      ))}
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE WEST END — the great double door you came in by, and the door out
// to the yard beside it, each a real opening in the stone
// ════════════════════════════════════════════════════════════════════
const YW = 1.3, YH = 2.35, EW = 2.7, EH = 2.9;

export function doorsOf(L) {
  return { yardZ: L.yardDoor.y * S + 0.4, entryZ: L.spawn.y * S };
}

function WestWall({ L, room, finish }) {
  const { zN, zS, xW } = room;
  const { yardZ, entryZ } = doorsOf(L);
  const { built, mats } = useWall((m, fx) => {
    const segs = [[zN - 0.5, yardZ - YW / 2], [yardZ + YW / 2, entryZ - EW / 2], [entryZ + EW / 2, zS + 0.5]];
    for (const [a, b] of segs) {
      if (b <= a) continue;
      m.box("stone", 0.5, WALL_H, b - a, [xW - 0.25, WALL_H / 2, (a + b) / 2]);
      const a2 = Math.max(a, zN), b2 = Math.min(b, zS);
      if (b2 > a2) {
        m.box("marble", 0.08, DADO_H, b2 - a2, [xW + 0.04, DADO_H / 2, (a2 + b2) / 2]);
        m.box("gold", 0.14, 0.07, b2 - a2, [xW + 0.07, DADO_H + 0.03, (a2 + b2) / 2]);
      }
    }
    m.box("stone", 0.5, WALL_H - YH, YW, [xW - 0.25, (YH + WALL_H) / 2, yardZ]);
    m.box("stone", 0.5, WALL_H - EH, EW, [xW - 0.25, (EH + WALL_H) / 2, entryZ]);
    m.box("trim", 0.44, 0.42, zS - zN, [xW + 0.22, WALL_H - 0.21, (zN + zS) / 2]);
    m.box("gold", 0.46, 0.07, zS - zN, [xW + 0.23, WALL_H - 0.46, (zN + zS) / 2]);
    // the yard door
    for (const s of [-1, 1]) {
      m.box("trim", 0.26, YH + 0.2, 0.26, [xW + 0.08, (YH + 0.2) / 2, yardZ + s * (YW / 2 + 0.13)]);
      m.box("gold", 0.06, YH, 0.06, [xW + 0.22, YH / 2, yardZ + s * (YW / 2 + 0.02)]);
    }
    m.box("trim", 0.4, 0.28, YW + 0.52, [xW + 0.05, YH + 0.14, yardZ]);
    m.box("gold", 0.42, 0.06, YW + 0.1, [xW + 0.06, YH - 0.02, yardZ]);
    m.box("marble", 0.7, 0.04, YW, [xW, 0.02, yardZ]);
    // ⭐ A REAL OPENING (visual review): stone reveals through the wall's depth,
    // a flagged step outside, and the two leaves standing ajar into the night
    for (const s of [-1, 1]) m.box("stone", 0.52, YH, 0.06, [xW - 0.26, YH / 2, yardZ + s * (YW / 2 - 0.03)]);
    m.box("stone", 0.52, 0.06, YW, [xW - 0.26, YH - 0.03, yardZ]);
    m.box("marble", 1.4, 0.05, YW + 0.4, [xW - 1.1, -0.01, yardZ]);
    for (const s of [-1, 1]) {
      const th = 1.05, w = YW / 2 - 0.02, hx = xW - 0.5, hz = yardZ + s * (YW / 2);
      const dx = -Math.sin(th), dz = -s * Math.cos(th);
      m.box("leaf", w, YH - 0.06, 0.05, [hx + (dx * w) / 2, (YH - 0.06) / 2, hz + (dz * w) / 2], [0, Math.atan2(s * Math.cos(th), -Math.sin(th)), 0]);
    }
    // the great door
    for (const s of [-1, 1]) {
      m.box("trim", 0.5, EH + 0.3, 0.42, [xW + 0.1, (EH + 0.3) / 2, entryZ + s * (EW / 2 + 0.21)]);
      m.box("gold", 0.52, 0.07, 0.44, [xW + 0.1, 1.2, entryZ + s * (EW / 2 + 0.21)]);
      m.add("gold", new THREE.TorusGeometry(0.1, 0.022, 8, 18), [xW - 0.02, 1.35, entryZ + s * 0.26], [0, Math.PI / 2, 0]);
      addSconce(m, fx, [xW + 0.0, entryZ + s * (EW / 2 + 0.95)], [1, 0], 2.25);
    }
    m.box("trim", 0.85, 0.46, EW + 1.0, [xW + 0.05, EH + 0.53, entryZ]);
    m.box("gold", 0.88, 0.07, EW + 1.05, [xW + 0.05, EH + 0.32, entryZ]);
    m.box("trim", 0.6, 0.34, 1.7, [xW + 0.0, EH + 0.93, entryZ]);
    m.cyl("gold", 0.3, 0.3, 0.1, 20, [xW + 0.33, EH + 0.95, entryZ], [0, 0, Math.PI / 2]);
    m.box("marble", 0.9, 0.05, EW, [xW, 0.025, entryZ]);
    addSconce(m, fx, [xW, (yardZ + YW / 2 + entryZ - EW / 2) / 2], [1, 0], 3.05);
  }, [L, room], () => ({
    leaf: new THREE.MeshStandardMaterial({ map: doorLeafTex(), roughness: 0.7, emissive: "#1a0e06", emissiveIntensity: 0.35 }),
    outside: new THREE.MeshBasicMaterial({ map: outsideTex(), toneMapped: false, fog: false }),
    pier: new THREE.MeshBasicMaterial({ map: plaqueTex("W'ILY'S PIER", GOLD_HI), toneMapped: false }),
    yard: new THREE.MeshBasicMaterial({ map: plaqueTex("THE MINERIUM", "#9ad46a"), toneMapped: false }),
    frameGlow: additive(poolTex(), "#d8c8ff", 0.16),
  }));
  const glow = useMemo(() => [
    // ⚠ no glow point in the yard doorway: it read as a white orb in a void (review)
    { x: xW + 0.6, y: EH + 0.95, z: entryZ, color: GOLD_HI, size: 1.0 },
  ], [xW, entryZ]);

  useEffect(() => { dressWall(mats, finish); }, [mats, finish]);
  return (
    <group>
      <WallFx built={built} mats={mats} finish={finish} />
      {[-1, 1].map((s) => (
        <mesh key={s} material={mats.leaf} position={[xW - 0.1, EH / 2, entryZ + s * (EW / 4)]}
              rotation={[0, Math.PI / 2, 0]} scale={[s, 1, 1]}>
          <planeGeometry args={[EW / 2 - 0.03, EH]} />
        </mesh>
      ))}
      <mesh material={mats.pier} position={[xW + 0.49, EH + 0.53, entryZ]} rotation={[0, Math.PI / 2, 0]}><planeGeometry args={[2.9, 0.44]} /></mesh>
      <mesh material={mats.outside} position={[xW - 1.75, YH * 0.62, yardZ]} rotation={[0, Math.PI / 2, 0]}><planeGeometry args={[YW * 2.2, YH * 1.5]} /></mesh>
      <mesh material={mats.frameGlow} position={[xW - 0.2, YH / 2, yardZ]} rotation={[0, Math.PI / 2, 0]}><planeGeometry args={[YW * 1.6, YH * 1.3]} /></mesh>
      <mesh material={mats.yard} position={[xW + 0.012, YH + 0.62, yardZ]} rotation={[0, Math.PI / 2, 0]}><planeGeometry args={[2.1, 0.33]} /></mesh>
      <Glows points={glow} size={1.6} />
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE EAST END — the Empire's capstone: a gold crest between two banners
// ════════════════════════════════════════════════════════════════════
const crestTex = () => canvasTex("hall-crest", 256, (ctx, W) => {
  const c = W / 2;
  const g = ctx.createRadialGradient(c, c, 10, c, c, c);
  g.addColorStop(0, "#fff0b8"); g.addColorStop(0.5, GOLD); g.addColorStop(0.92, "#6a4a10"); g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g; ctx.beginPath(); ctx.arc(c, c, c - 2, 0, 7); ctx.fill();
  ctx.strokeStyle = "#3a2608"; ctx.lineWidth = 6; ctx.beginPath(); ctx.arc(c, c, c * 0.78, 0, 7); ctx.stroke();
  ctx.font = `900 130px ${FONT}`; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillStyle = "#4a2a8a"; ctx.fillText("◈", c, c + 8);
});

function EastWall({ L, room, finish }) {
  const { zN, zS, xE } = room;
  const zc = (zN + zS) / 2;
  const empire = L.wings.find((w) => w.game === "empire") || { tint: "#8a5cd6", icon: "◈" };
  const { built, mats } = useWall((m, fx) => {
    m.box("stone", 0.5, WALL_H, zS - zN + 1, [xE + 0.25, WALL_H / 2, zc]);
    m.box("marble", 0.08, DADO_H, zS - zN, [xE - 0.04, DADO_H / 2, zc]);
    m.box("gold", 0.14, 0.07, zS - zN, [xE - 0.07, DADO_H + 0.03, zc]);
    m.box("trim", 0.44, 0.42, zS - zN, [xE - 0.22, WALL_H - 0.21, zc]);
    m.box("gold", 0.46, 0.07, zS - zN, [xE - 0.23, WALL_H - 0.46, zc]);
    m.box("marble", 0.1, 3.4, 3.0, [xE - 0.05, 3.3, zc]);
    for (const s of [-1, 1]) m.box("trim", 0.26, 3.7, 0.26, [xE - 0.13, 3.3, zc + s * 1.6]);
    m.box("trim", 0.3, 0.3, 3.5, [xE - 0.15, 5.1, zc]);
    m.box("gold", 0.32, 0.06, 3.5, [xE - 0.16, 4.93, zc]);
    m.box("marble", 0.34, 0.5, 3.2, [xE - 0.17, 1.35, zc]);
    // ⚠ A RAISED MEDALLION, not a painted disc (visual review): a gilt drum
    // with a rolled rim, the crest's face set into its front
    m.cyl("gold", 1.02, 1.08, 0.14, 40, [xE - 0.17, 3.3, zc], [0, 0, Math.PI / 2]);
    m.add("gold", new THREE.TorusGeometry(1.03, 0.055, 8, 40), [xE - 0.25, 3.3, zc], [0, Math.PI / 2, 0]);
    for (const s of [-1, 1]) {
      m.box("gold", 0.06, 0.06, 1.4, [xE - 0.18, 5.7, zc + s * 3.2]);
      addSconce(m, fx, [xE, zc + s * 5.0], [-1, 0], 3.05);
    }
  }, [L, room], () => ({
    crest: new THREE.MeshBasicMaterial({ map: crestTex(), transparent: true, toneMapped: false }),
    banner: new THREE.MeshStandardMaterial({
      map: wingBannerTex(empire.tint, empire.icon || "◈"), side: THREE.DoubleSide, transparent: true, alphaTest: 0.4,
      roughness: 0.9, emissive: empire.tint, emissiveIntensity: 0.12,
    }),
    name: new THREE.MeshBasicMaterial({ map: plaqueTex("THE EMPIRE ENDURES", GOLD_HI), toneMapped: false }),
    halo: additive(poolTex(), GOLD_HI, 0.3),
  }));
  useEffect(() => { dressWall(mats, finish); }, [mats, finish]);
  return (
    <group>
      <WallFx built={built} mats={mats} finish={finish} />
      <mesh material={mats.halo} position={[xE - 0.11, 3.3, zc]} rotation={[0, -Math.PI / 2, 0]}><planeGeometry args={[4.2, 4.2]} /></mesh>
      <mesh material={mats.crest} position={[xE - 0.252, 3.3, zc]} rotation={[0, -Math.PI / 2, 0]}><planeGeometry args={[2.1, 2.1]} /></mesh>
      <mesh material={mats.name} position={[xE - 0.35, 1.35, zc]} rotation={[0, -Math.PI / 2, 0]}><planeGeometry args={[3.0, 0.46]} /></mesh>
      {[-1, 1].map((s) => (
        <mesh key={s} material={mats.banner} position={[xE - 0.2, 4.4, zc + s * 3.2]} rotation={[0, -Math.PI / 2, 0]}>
          <planeGeometry args={[1.25, 2.5]} />
        </mesh>
      ))}
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE CEILING — heavy beams at every gutter, a lighter one mid-bay, two
// runs along the hall, and a gilt rosette in every coffer
// ════════════════════════════════════════════════════════════════════
function Ceiling({ L, room }) {
  const { zN, zS, xW, xE } = room;
  const { built, mats } = useWall((m) => {
    const d = zS - zN, zc = (zN + zS) / 2, len = xE - xW + 1, cx = (xW + xE) / 2;
    m.box("stone", len, 0.4, d + 1, [cx, CEIL_Y + 0.2, zc]);
    for (const gx of guttersOf(L)) {
      m.box("trim", 0.8, 0.52, d, [gx, CEIL_Y - 0.26, zc]);
      m.box("gold", 0.34, 0.04, d, [gx, CEIL_Y - 0.54, zc]);
    }
    for (const w of L.wings) {
      const bx = ((w.x0 + w.x1) / 2) * S;
      m.box("trim", 0.44, 0.34, d, [bx, CEIL_Y - 0.17, zc]);
      for (const sx of [-1, 1]) for (const f of [1 / 6, 3 / 6, 5 / 6]) {
        m.cyl("gold", 0.3, 0.2, 0.08, 16, [bx + sx * 3.75, CEIL_Y - 0.04, zN + d * f]);
      }
    }
    for (const f of [1 / 3, 2 / 3]) m.box("trim", len, 0.34, 0.44, [cx, CEIL_Y - 0.17, zN + d * f]);
  }, [L, room], () => ({
    stone: new THREE.MeshStandardMaterial({ map: stoneTex(), color: "#8e84a6", roughness: 0.95 }),
    trim: new THREE.MeshStandardMaterial({ color: "#c8c0d6", roughness: 0.7 }),
  }));
  return <Merged geos={built.geos} mats={mats} />;
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE SHELL — the whole room. `north` and `south` are what hangs on
// those walls (the painted deeds, the gallery frames), so they step
// aside with the stone they hang on.
// ════════════════════════════════════════════════════════════════════
export function HallShell({ L, north = null, south = null, finish = null }) {
  const room = useMemo(() => roomOf(L), [L]);
  return (
    <group>
      <HallFloor L={L} finish={finish} />
      <CutGroup side="N" room={room}>
        <LongWall L={L} room={room} side="N" part="full" finish={finish} />
        <BayDressing L={L} room={room} finish={finish} />
        {north}
      </CutGroup>
      <LongWall L={L} room={room} side="S" part="low" finish={finish} />
      <CutGroup side="S" room={room}>
        <LongWall L={L} room={room} side="S" part="upper" finish={finish} />
        {south}
      </CutGroup>
      <CutGroup side="W" room={room}><WestWall L={L} room={room} finish={finish} /></CutGroup>
      <CutGroup side="E" room={room}><EastWall L={L} room={room} finish={finish} /></CutGroup>
      <CutGroup side="C" room={room}><Ceiling L={L} room={room} /></CutGroup>
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE DEEDS — every trophy a real object on a real plinth
// ────────────────────────────────────────────────────────────────────
// Legendary → a gilded cup. Mythic → a gilt figure holding its gem aloft.
// Rare/Epic → a painting on the Wall of Renown (or an easel, when a wing
// has more than the wall holds). Common/Uncommon → a struck medal on a
// ribbon, standing on a low display stand.
// ⚑ EARNED vs NOT, visibly: earned is gold, lit by a painted spot on its
// plinth and a pool on the marble; unearned is dim stone and bronze, the
// cup under a violet veil — the chase, standing in your own hall. A
// visitor sees earned deeds only (PierHome's 2D rule, unchanged).
// ⚑ Stand-offset: each floor deed sits 0.4 north of its tile, so the
// player stands in front of it rather than inside it.
// ════════════════════════════════════════════════════════════════════
const DEED_Z = -0.4;
const SLOPE_N = [Math.cos(0.38), Math.sin(0.38)];   // the lectern slope normal (y, z)

function plane(w, h) { return new THREE.PlaneGeometry(w, h); }

function deedMat(key, atlas) {
  if (key === "trim") return new THREE.MeshStandardMaterial({ color: "#e6e0ec", roughness: 0.55 });
  if (key === "ped") return new THREE.MeshStandardMaterial({ map: stoneTex(), color: STONE, roughness: 0.85 });
  if (key === "pedDim") return new THREE.MeshStandardMaterial({ map: stoneTex(), color: "#6e6878", roughness: 0.95 });
  if (key === "gold") return new THREE.MeshStandardMaterial({ color: GOLD, emissive: "#7a5410", emissiveIntensity: 0.8, metalness: 0.9, roughness: 0.28 });
  if (key === "bronze") return new THREE.MeshStandardMaterial({ color: "#8a7050", metalness: 0.6, roughness: 0.5, emissive: "#2a1c0c", emissiveIntensity: 0.4 });
  if (key === "veil") return new THREE.MeshStandardMaterial({ color: "#5a4a78", roughness: 0.95, emissive: "#1c1030", emissiveIntensity: 0.45, side: THREE.DoubleSide });
  if (key === "shroud") return new THREE.MeshStandardMaterial({ color: "#5a4a70", roughness: 1, emissive: "#221634", emissiveIntensity: 0.5 });
  if (key === "bust") return new THREE.MeshStandardMaterial({ map: stoneTex(), color: "#e4dcec", roughness: 0.7, emissive: "#2a2230", emissiveIntensity: 0.35 });
  if (key === "niche") return new THREE.MeshStandardMaterial({ color: "#1c1628", roughness: 1, emissive: "#0e0818", emissiveIntensity: 0.3 });
  if (key === "iron") return new THREE.MeshStandardMaterial({ color: "#221c2c", roughness: 0.5, metalness: 0.7 });
  if (key === "wood") return new THREE.MeshStandardMaterial({ map: timberTex(), color: "#7a5a3c", roughness: 0.9 });
  if (key === "matte") return new THREE.MeshStandardMaterial({ color: "#120e18", roughness: 1 });
  if (key === "face") return new THREE.MeshStandardMaterial({ map: atlas.tex, emissiveMap: atlas.tex, emissive: "#ffffff", emissiveIntensity: 0.55, roughness: 0.5 });
  if (key === "shadow") return new THREE.MeshBasicMaterial({ map: shadowTex(), transparent: true, opacity: 0.7, depthWrite: false });
  if (key === "wallpool") return additive(poolTex(), "#ffd79a", 0.4);
  const [kind, arg] = key.split("|");
  if (kind === "spot") return additive(poolTex(), arg, 0.75);
  if (kind === "floor") return additive(poolTex(), arg, 0.42);
  if (kind === "gem") return new THREE.MeshStandardMaterial({ color: arg, emissive: arg, emissiveIntensity: 1.3, roughness: 0.2, metalness: 0.3, toneMapped: false });
  if (kind === "ribbon") return new THREE.MeshStandardMaterial({ color: arg, roughness: 0.9, emissive: arg, emissiveIntensity: 0.15 });
  return new THREE.MeshStandardMaterial({ color: "#ff00ff" });
}

function useDeedMats(geos, atlas) {
  const mats = useMemo(() => {
    const out = {};
    for (const k of Object.keys(geos)) out[k] = deedMat(k, atlas);
    return out;
  }, [geos, atlas]);
  useEffect(() => () => disposeMats(mats), [mats]);
  return mats;
}

function buildCup(m, X, top, Z, gem) {
  m.cyl("gold", 0.13, 0.15, 0.05, 18, [X, top + 0.025, Z]);
  m.cyl("gold", 0.035, 0.05, 0.2, 10, [X, top + 0.15, Z]);
  m.add("gold", new THREE.SphereGeometry(0.055, 12, 10), [X, top + 0.26, Z]);
  m.cyl("gold", 0.21, 0.08, 0.26, 20, [X, top + 0.4, Z]);
  m.add("gold", new THREE.TorusGeometry(0.205, 0.018, 6, 24), [X, top + 0.53, Z], [Math.PI / 2, 0, 0]);
  m.add("gold", new THREE.TorusGeometry(0.085, 0.02, 6, 12, Math.PI), [X + 0.2, top + 0.41, Z], [0, 0, -Math.PI / 2]);
  m.add("gold", new THREE.TorusGeometry(0.085, 0.02, 6, 12, Math.PI), [X - 0.2, top + 0.41, Z], [0, 0, Math.PI / 2]);
  // set INTO the bowl as a cabochon, not a bead stuck on its side (review)
  m.add(gem, new THREE.SphereGeometry(0.05, 12, 10), [X, top + 0.42, Z + 0.15], [0.35, 0, 0], [1, 1, 0.38]);
}

function buildFigure(m, X, top, Z, gem) {
  m.cyl("gold", 0.17, 0.19, 0.08, 18, [X, top + 0.04, Z]);
  m.cyl("gold", 0.12, 0.14, 0.08, 18, [X, top + 0.12, Z]);
  m.cyl("gold", 0.05, 0.12, 0.42, 14, [X, top + 0.37, Z]);
  m.add("gold", new THREE.SphereGeometry(0.075, 14, 12), [X, top + 0.64, Z]);
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    m.cyl("gold", 0, 0.02, 0.07, 6, [X + Math.cos(a) * 0.05, top + 0.73, Z + Math.sin(a) * 0.05]);
  }
  // one arm raised, the gem GRIPPED in a gold claw at its end. ⚠ A 3.5 cm arm
  // touching the gem at a single point read as a gem hovering over the figure
  // (visual review, 2026-09-14).
  m.add("gold", new THREE.SphereGeometry(0.045, 10, 8), [X + 0.075, top + 0.58, Z]);
  m.box("gold", 0.085, 0.34, 0.085, [X + 0.1, top + 0.66, Z], [0, 0, -0.28]);
  m.cyl("gold", 0.095, 0.05, 0.1, 12, [X + 0.15, top + 0.85, Z]);
  // ⚠ prongs THICK enough to read at walking distance, overlapping the gem
  // (second review: a thin stalk still read as the gem perched, not held)
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    m.box("gold", 0.035, 0.14, 0.035, [X + 0.15 + Math.cos(a) * 0.065, top + 0.93, Z + Math.sin(a) * 0.065], [Math.sin(a) * 0.3, 0, -Math.cos(a) * 0.3]);
  }
  m.add(gem, new THREE.OctahedronGeometry(0.08, 0), [X + 0.15, top + 0.96, Z]);
}

// ⚠ A CLOTH DRAPED OVER THE CUP, not a black cone. Nine flat facets in near-
// black read as a witch's hat, or a hole (visual review). A lathed silhouette
// of cloth over a cup, with folds pressed into its hem.
const VEIL_PROFILE = [[0.31, 0], [0.3, 0.03], [0.25, 0.1], [0.215, 0.24], [0.2, 0.36], [0.235, 0.47], [0.2, 0.53], [0.11, 0.6], [0.035, 0.635], [0, 0.645]];
function veilGeo() {
  const g = new THREE.LatheGeometry(VEIL_PROFILE.map(([r, y]) => new THREE.Vector2(r, y)), 42);
  const p = g.attributes.position;
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    if (Math.hypot(x, z) < 1e-4) continue;
    // six deep folds, strongest at the hem (second review: 7% read as a smooth bell)
    const k = 1 + Math.sin(Math.atan2(z, x) * 6) * 0.15 * Math.max(0.25, 1 - y / 0.55);
    p.setX(i, x * k); p.setZ(i, z * k);
  }
  g.computeVertexNormals();
  return g;
}
function buildVeil(m, X, top, Z) {
  m.add("veil", veilGeo(), [X, top, Z]);
  // the cloth pools on the plinth, and a gold cord ties it at the cup's neck
  m.add("veil", new THREE.TorusGeometry(0.3, 0.045, 6, 30), [X, top + 0.03, Z], [Math.PI / 2, 0, 0]);
  m.add("gold", new THREE.TorusGeometry(0.212, 0.016, 6, 28), [X, top + 0.5, Z], [Math.PI / 2, 0, 0]);
}

export function FloorDeeds({ L, atlas, earned, isOwnHome }) {
  const built = useMemo(() => {
    const m = new Merger();
    const glows = [];
    const wallRows = new Set(L.wallRows);
    for (const t of L.trophies) {
      if (t.kind === "painting" && wallRows.has(t.y)) continue;
      const e = earned.has(t.id);
      if (!e && !isOwnHome) continue;
      const info = L.deedInfo(t.id, e);
      const X = t.x * S, Z = t.y * S + (t.kind === "painting" ? -0.3 : DEED_Z);
      const cell = atlas.index.get(t.id) ?? 0;
      const gem = `gem|${info.rarityTint}`;
      m.plane("shadow", 1.1, 1.1, [X, 0.011, Z], [-Math.PI / 2, 0, 0]);
      if (t.kind === "plinth") {
        m.box("trim", 0.64, 0.12, 0.64, [X, 0.06, Z]);
        m.box(e ? "ped" : "pedDim", 0.46, 0.8, 0.46, [X, 0.52, Z]);
        m.box("trim", 0.62, 0.1, 0.62, [X, 0.97, Z]);
        m.box(e ? "gold" : "bronze", 0.48, 0.05, 0.48, [X, 0.22, Z]);
        m.box(e ? "gold" : "bronze", 0.48, 0.05, 0.48, [X, 0.86, Z]);
        m.add("face", cellUV(plane(0.36, 0.36), atlas, cell), [X, 0.54, Z + 0.232]);
        const top = 1.02;
        if (!e) buildVeil(m, X, top, Z);
        else if (info.order >= 5) buildFigure(m, X, top, Z, gem);
        else buildCup(m, X, top, Z, gem);
        if (e) {
          m.plane(`spot|${info.rarityTint}`, 0.9, 0.9, [X, 1.024, Z], [-Math.PI / 2, 0, 0]);
          m.plane(`floor|${info.rarityTint}`, 1.9, 1.9, [X, 0.013, Z], [-Math.PI / 2, 0, 0]);
          glows.push({ x: X, y: top + 0.55, z: Z, color: info.rarityTint, size: 0.7 });
        }
      } else if (t.kind === "medallion") {
        m.box("trim", 0.52, 0.08, 0.42, [X, 0.04, Z]);
        m.box(e ? "ped" : "pedDim", 0.34, 0.6, 0.28, [X, 0.38, Z]);
        m.box("trim", 0.48, 0.06, 0.38, [X, 0.71, Z]);
        for (const s of [-1, 1]) m.box("iron", 0.025, 0.62, 0.025, [X + s * 0.2, 1.03, Z - 0.08]);
        m.box(e ? "gold" : "bronze", 0.46, 0.03, 0.03, [X, 1.33, Z - 0.08]);
        m.box(`ribbon|${t.tint}`, 0.1, 0.18, 0.012, [X, 1.24, Z - 0.07]);
        m.cyl(e ? "gold" : "bronze", 0.2, 0.2, 0.04, 28, [X, 0.97, Z - 0.06], [Math.PI / 2, 0, 0]);
        m.add("face", cellUV(plane(0.27, 0.27), atlas, cell), [X, 0.97, Z - 0.035]);
        if (e) {
          m.plane(`spot|${info.rarityTint}`, 0.5, 0.42, [X, 0.745, Z], [-Math.PI / 2, 0, 0]);
          m.plane(`floor|${info.rarityTint}`, 1.3, 1.3, [X, 0.013, Z], [-Math.PI / 2, 0, 0]);
          glows.push({ x: X, y: 0.97, z: Z, color: info.rarityTint, size: 0.45 });
        }
      } else {
        // a low display lectern — the overflow of a crowded Wall of Renown.
        // ⚠ NOT AN EASEL. At 1.9 tall an easel stood across the lower third of
        // the wall frame behind it however it was staggered (visual reviews ×2).
        // This one tops out under the frames, the deed laid on its slope.
        const a = 0.85, ny = Math.cos(a), nz = Math.sin(a), top = 0.7;
        m.box("trim", 0.46, 0.06, 0.36, [X, 0.03, Z]);
        m.box("wood", 0.08, 0.66, 0.08, [X, 0.36, Z]);
        m.box("wood", 0.74, 0.05, 0.58, [X, top, Z], [a, 0, 0]);
        m.box(e ? "gold" : "bronze", 0.66, 0.03, 0.52, [X, top + 0.04 * ny, Z + 0.04 * nz], [a, 0, 0]);
        m.box(e ? "matte" : "shroud", 0.58, 0.01, 0.44, [X, top + 0.058 * ny, Z + 0.058 * nz], [a, 0, 0]);
        m.add("face", cellUV(plane(0.4, 0.4), atlas, cell), [X, top + 0.066 * ny, Z + 0.066 * nz], [a - Math.PI / 2, 0, 0]);
        if (e) {
          m.plane(`floor|${info.rarityTint}`, 1.5, 1.5, [X, 0.013, Z], [-Math.PI / 2, 0, 0]);
        }
      }
    }
    const geos = m.build();
    ["ped", "pedDim"].forEach((k) => geos[k] && boxUV(geos[k], 1.2));
    if (geos.wood) boxUV(geos.wood, 2);
    return { geos, glows };
  // ⚑ OW-42: keyed on the DEEDS, not the layout — a wall going up is not a
  // reason to rebuild every plinth (composeHall hands back the same array)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [L.trophies, L.wallRows, L.deedInfo, atlas, earned, isOwnHome]);
  useEffect(() => () => disposeGeos(built.geos), [built]);
  const mats = useDeedMats(built.geos, atlas);
  return (
    <group>
      <Merged geos={built.geos} mats={mats} />
      {built.glows.length > 0 && <Glows points={built.glows} size={0.7} />}
    </group>
  );
}

// The paintings on the Wall of Renown — hung flush, each under a brass lamp.
export function WallDeeds({ L, atlas, earned, isOwnHome }) {
  const built = useMemo(() => {
    const zN = roomOf(L).zN;
    const m = new Merger();
    const glows = [];
    const wallRows = new Set(L.wallRows);
    for (const t of L.trophies) {
      if (t.kind !== "painting" || !wallRows.has(t.y)) continue;
      const e = earned.has(t.id);
      if (!e && !isOwnHome) continue;
      const X = t.x * S;
      const cell = atlas.index.get(t.id) ?? 0;
      m.box(e ? "gold" : "bronze", 1.0, 1.22, 0.08, [X, 2.0, zN + 0.04]);
      m.box(e ? "matte" : "shroud", 0.84, 1.06, 0.02, [X, 2.0, zN + 0.09]);
      m.add("face", cellUV(plane(0.74, 0.74), atlas, cell), [X, 2.06, zN + 0.101]);
      // ⚠ an unearned deed is CURTAINED, not a black square in a black frame
      // (visual review): a velvet pelmet half-drawn over it, on a gilt rod
      if (!e) {
        m.box("veil", 0.92, 0.5, 0.035, [X, 2.36, zN + 0.13]);
        m.box("gold", 1.04, 0.04, 0.04, [X, 2.63, zN + 0.14]);
        for (const sd of [-1, 1]) {
          m.add("gold", new THREE.SphereGeometry(0.035, 8, 6), [X + sd * 0.53, 2.63, zN + 0.14]);
          m.cyl("gold", 0, 0.04, 0.11, 6, [X + sd * 0.42, 2.05, zN + 0.15]);          // tassels
        }
      }
      if (e) {
        m.box("gold", 0.5, 0.06, 0.13, [X, 2.72, zN + 0.22]);
        m.box("gold", 0.03, 0.03, 0.2, [X, 2.69, zN + 0.1]);
        m.plane("wallpool", 1.5, 2.1, [X, 2.15, zN + 0.012]);
        glows.push({ x: X, y: 2.66, z: zN + 0.26, color: "#ffe2a8", size: 0.45 });
      }
    }
    // ⭐ AN EMPTY STRETCH OF THE WALL IS A NICHE WITH A BUST IN IT. A bay with one
    // deed in it read as unfinished rather than quiet (visual review, 2026-09-14).
    // Every other free tile of each bay, clear of the frames by a tile.
    // ⚠ OW-34: the niches are the LAYOUT's (`L.nicheXs`, PierHome), laid out
    // around every wall deed whether this viewer sees it or not — a hanging
    // may be put on any other plain stretch, so a niche must never appear
    // under one in somebody else's view of the room. A visitor sees plain
    // stone where an unearned deed would hang.
    for (const x of (L.nicheXs || [])) {
      {
        const NX = x * S;
        m.box("trim", 1.1, 1.9, 0.1, [NX, 1.62, zN + 0.05]);
        m.box("niche", 0.86, 1.64, 0.02, [NX, 1.58, zN + 0.105]);
        m.plane("wallpool", 1.1, 1.7, [NX, 1.7, zN + 0.118]);
        m.box("trim", 1.2, 0.14, 0.18, [NX, 2.6, zN + 0.09]);
        m.box("gold", 0.18, 0.22, 0.2, [NX, 2.6, zN + 0.1]);                        // keystone
        m.box("trim", 0.5, 0.46, 0.3, [NX, 1.0, zN + 0.2]);                         // plinth
        m.box("gold", 0.52, 0.04, 0.32, [NX, 1.24, zN + 0.2]);
        // ⚠ A BUST HAS A FACE (second review: faceless grey pawns). Shoulders,
        // neck, a head with a brow, a nose and its hair.
        m.add("bust", new THREE.SphereGeometry(0.28, 16, 10), [NX, 1.44, zN + 0.2], [0, 0, 0], [1.2, 0.55, 0.72]);
        m.cyl("bust", 0.08, 0.1, 0.2, 10, [NX, 1.64, zN + 0.21]);
        m.add("bust", new THREE.SphereGeometry(0.16, 16, 12), [NX, 1.84, zN + 0.22], [0, 0, 0], [0.85, 1.05, 0.95]);
        m.add("bust", new THREE.SphereGeometry(0.17, 14, 10), [NX, 1.9, zN + 0.19], [0, 0, 0], [0.9, 0.82, 0.9]);
        m.box("bust", 0.17, 0.03, 0.04, [NX, 1.89, zN + 0.36]);                     // brow
        m.box("bust", 0.04, 0.07, 0.06, [NX, 1.82, zN + 0.38]);                     // nose
      }
    }
    const geos = m.build();
    if (geos.bust) boxUV(geos.bust, 1.5);
    return { geos, glows };
  // ⚑ OW-42: keyed on what it draws — a wall going up elsewhere is not a
  // reason to rebuild every bust (the Hall's layout object is new each edit)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [L.trophies, (L.nicheXs || []).join(), L.wallRows, L.hallTop, atlas, earned, isOwnHome]);
  useEffect(() => () => disposeGeos(built.geos), [built]);
  const mats = useDeedMats(built.geos, atlas);
  return (
    <group>
      <Merged geos={built.geos} mats={mats} />
      {built.glows.length > 0 && <Glows points={built.glows} size={0.7} />}
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE GALLERY WALL — real frames, flush on the south wall
// ────────────────────────────────────────────────────────────────────
// A hung piece shows its art if the image is data: or same-origin (the
// only kinds WebGL may read). Mint art is CDN-hosted with no CORS, so in
// practice the frame holds a painted study carrying the title and the
// Sigil Code — see `artTex`. An empty slot is an empty frame and a brass
// plate. Everything the owner-only rules decide happens in PierHome.
// ════════════════════════════════════════════════════════════════════
function readableImage(url) {
  if (!url || typeof window === "undefined") return false;
  if (url.startsWith("data:")) return true;
  try { return new URL(url, window.location.href).origin === window.location.origin; } catch { return false; }
}

const _imgCache = new Map();
function useArtImage(url) {
  const ok = readableImage(url);
  const [img, setImg] = useState(ok ? _imgCache.get(url) || null : null);
  useEffect(() => {
    if (!ok) { setImg(null); return undefined; }
    if (_imgCache.has(url)) { setImg(_imgCache.get(url)); return undefined; }
    let alive = true;
    const im = new Image();
    im.onload = () => { _imgCache.set(url, im); if (alive) setImg(im); };
    im.onerror = () => { if (alive) setImg(null); };
    im.src = url;
    return () => { alive = false; };
  }, [url, ok, setImg]);
  return img;
}

function FrameSlot({ slot, piece, zS }) {
  const img = useArtImage(piece?.image);
  const filled = !!piece;
  const mats = useMemo(() => {
    const art = filled ? artTex(piece, img) : null;
    return {
      bar: filled
        ? new THREE.MeshStandardMaterial({ color: GOLD, emissive: "#7a5410", emissiveIntensity: 0.75, metalness: 0.9, roughness: 0.28 })
        // ⚠ an empty frame is still a gilt frame: on the moonlit south wall a
        // dark bronze read as a black hole in the stone
        : new THREE.MeshStandardMaterial({ color: "#a8884a", emissive: "#3a2a0c", emissiveIntensity: 0.6, metalness: 0.75, roughness: 0.4 }),
      back: new THREE.MeshStandardMaterial({ color: filled ? "#0a0810" : "#3a3048", roughness: 1 }),
      art: art ? new THREE.MeshStandardMaterial({ map: art, emissiveMap: art, emissive: "#ffffff", emissiveIntensity: 0.4, roughness: 0.6 }) : null,
      brass: new THREE.MeshStandardMaterial({ color: "#b08a3a", metalness: 0.8, roughness: 0.35 }),
      plate: new THREE.MeshBasicMaterial({
        map: plateTex(filled ? String(piece.title || "Untitled").slice(0, 24).toUpperCase() : "VACANT"),
        toneMapped: false,
      }),
      pool: additive(poolTex(), piece?.prov?.color || "#ffd79a", filled ? 0.45 : 0.14),
    };
  }, [filled, piece, img]);
  useEffect(() => () => disposeMats(mats), [mats]);
  const X = slot.x * S;
  return (
    // local +z points into the room
    // ⚠ HUNG 0.55 HIGHER: at 1.8 the medallion stands in front covered the bottom
    // of every frame and its brass title plate (visual review, 2026-09-14)
    <group position={[X, 0.55, zS]} rotation={[0, Math.PI, 0]}>
      <mesh material={mats.pool} position={[0, 1.9, 0.006]}><planeGeometry args={[1.9, 2.2]} /></mesh>
      <mesh material={mats.back} position={[0, 1.8, 0.012]}><boxGeometry args={[1.02, 0.82, 0.02]} /></mesh>
      {mats.art && <mesh material={mats.art} position={[0, 1.8, 0.026]}><planeGeometry args={[1.0, 0.8]} /></mesh>}
      <mesh material={mats.bar} position={[0, 2.26, 0.05]}><boxGeometry args={[1.26, 0.12, 0.1]} /></mesh>
      <mesh material={mats.bar} position={[0, 1.34, 0.05]}><boxGeometry args={[1.26, 0.12, 0.1]} /></mesh>
      <mesh material={mats.bar} position={[-0.57, 1.8, 0.05]}><boxGeometry args={[0.12, 0.8, 0.1]} /></mesh>
      <mesh material={mats.bar} position={[0.57, 1.8, 0.05]}><boxGeometry args={[0.12, 0.8, 0.1]} /></mesh>
      <mesh material={mats.brass} position={[0, 1.12, 0.012]}><boxGeometry args={[0.56, 0.15, 0.02]} /></mesh>
      <mesh material={mats.plate} position={[0, 1.12, 0.024]}><planeGeometry args={[0.52, 0.12]} /></mesh>
      {filled && (
        <>
          <mesh material={mats.brass} position={[0, 2.48, 0.22]}><boxGeometry args={[0.5, 0.06, 0.13]} /></mesh>
          <mesh material={mats.brass} position={[0, 2.44, 0.1]}><boxGeometry args={[0.03, 0.03, 0.2]} /></mesh>
        </>
      )}
    </group>
  );
}

export function GalleryFrames({ L, decorMap, galleryById }) {
  const zS = roomOf(L).zS;
  return L.frameSlots.map((slot) => {
    const pieceId = decorMap?.[slot.slotId];
    const piece = pieceId ? galleryById?.[pieceId] || null : null;
    return <FrameSlot key={slot.slotId} slot={slot} piece={piece} zS={zS} />;
  });
}

// ════════════════════════════════════════════════════════════════════
// THE DEED BOOK — a lectern, the book open on it, lit from its pages
// ════════════════════════════════════════════════════════════════════
export function DeedBook({ L }) {
  // ⚑ a full step north of its tile: the reader stands IN FRONT of the lectern,
  // so the open book is not hidden behind their own (always-on-top) sprite
  const X = L.deedBook.x * S, Z = L.deedBook.y * S - 1.0;
  const A = 0.38;
  const n = SLOPE_N;                               // slope normal (y, z)
  const built = useMemo(() => {
    const m = new Merger();
    m.plane("shadow", 1.3, 1.1, [X, 0.011, Z], [-Math.PI / 2, 0, 0]);
    m.box("trim", 0.72, 0.1, 0.56, [X, 0.05, Z]);
    m.cyl("wood", 0.08, 0.13, 0.86, 12, [X, 0.53, Z]);
    m.cyl("gold", 0.13, 0.13, 0.05, 14, [X, 0.95, Z]);
    m.cyl("gold", 0.15, 0.15, 0.05, 14, [X, 0.13, Z]);
    m.box("wood", 0.76, 0.06, 0.54, [X, 1.02, Z], [A, 0, 0]);
    m.box("leather", 0.68, 0.035, 0.47, [X, 1.02 + 0.045 * n[0], Z + 0.045 * n[1]], [A, 0, 0]);
    // the block of pages the open spread lies on — a book, not a sheet (review)
    m.box("paper", 0.62, 0.05, 0.42, [X, 1.02 + 0.075 * n[0], Z + 0.075 * n[1]], [A, 0, 0]);
    const geos = m.build();
    if (geos.wood) boxUV(geos.wood, 2);
    return geos;
  }, [X, Z, n]);
  const mats = useMemo(() => ({
    trim: new THREE.MeshStandardMaterial({ color: "#e6e0ec", roughness: 0.55 }),
    wood: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#5a3c26", roughness: 0.8 }),
    gold: new THREE.MeshStandardMaterial({ color: GOLD, emissive: "#7a5410", emissiveIntensity: 0.8, metalness: 0.9, roughness: 0.28 }),
    leather: new THREE.MeshStandardMaterial({ color: "#3a1a2e", roughness: 0.8 }),
    shadow: new THREE.MeshBasicMaterial({ map: shadowTex(), transparent: true, opacity: 0.75, depthWrite: false }),
    paper: new THREE.MeshStandardMaterial({ color: "#e8dcc0", roughness: 0.9, emissive: "#6a5020", emissiveIntensity: 0.35 }),
    pages: new THREE.MeshStandardMaterial({ map: bookTex(), emissiveMap: bookTex(), emissive: "#ffffff", emissiveIntensity: 0.55, roughness: 0.85 }),
    halo: additive(poolTex(), GOLD_HI, 0.3),
    floor: additive(poolTex(), GOLD_HI, 0.3),
  }), []);
  useEffect(() => () => disposeGeos(built), [built]);
  useEffect(() => () => disposeMats(mats), [mats]);
  useEffect(() => {
    const fn = (t) => { mats.halo.opacity = 0.22 + 0.1 * Math.sin(t * 1.6); };
    BUILD_TICKS.add(fn);
    return () => { BUILD_TICKS.delete(fn); };
  }, [mats]);
  const py = 1.02 + 0.101 * n[0], pz = Z + 0.101 * n[1];
  return (
    <group>
      <Merged geos={built} mats={mats} />
      <mesh material={mats.pages} position={[X, py, pz]} rotation={[-Math.PI / 2 + A, 0, 0]}><planeGeometry args={[0.64, 0.43]} /></mesh>
      {/* ⚠ no halo plane and no glow sprite over the book: both read as a ball of
          light hovering beside the lectern (second review). The pages glow. */}
      <mesh material={mats.floor} position={[X, 0.017, Z + 0.3]} rotation={[-Math.PI / 2, 0, 0]}><planeGeometry args={[2.8, 2.8]} /></mesh>
    </group>
  );
}
